<template>
  <MainLayout>
    <Head>
      <title>{{ title }}</title>
    </Head>
    <template v-slot:header>
      <span ref="topAnchor">
        {{ title }}
      </span>
    </template>

    <FormWrapper grid>
      <form @submit.prevent="submit">
        <FormInput name="sku"
                   v-model="form.sku"
                   :errors="errors"
                   class="sm:col-span-4"/>

        <FormTextarea name="description"
                   v-model="form.description"
                   :errors="errors"
                   :rows="8"
                   class="sm:col-span-6"/>

        <FormTextarea name="additional_data.seo_meta_description"
                      label="SEO Meta Description"
                      v-model="form.additional_data.seo_meta_description"
                      :errors="errors"
                      :rows="3"
                      class="sm:col-span-6"/>

        <FormInput name="retail_price"
                   v-model="form.retail_price"
                   :errors="errors"
                   class="sm:col-span-4"/>

        <FormInput name="additional_data.inventory_quantity"
                   label="Inventory Quantity"
                   v-model="form.additional_data.inventory_quantity"
                   :errors="errors"
                   class="sm:col-span-3"/>

        <FormInput name="additional_data.item_weight"
                   label="Item Weight"
                   v-model="form.additional_data.item_weight"
                   :errors="errors"
                   class="sm:col-span-3"/>

        <div class="section">
          <FormInput v-for="item in imageFields"
                     :key="item.field"
                     :name="`additional_data.${item.field}`"
                     :label="item.label"
                     v-model="form.additional_data[item.field]"
                     :errors="errors"
                     class="sm:col-span-3"
                     />
        </div>

        <div class="section">
          <FormInput name="additional_data.install_video_url_1"
                     label="Install Video 1"
                     v-model="form.additional_data.install_video_url_1"
                     :errors="errors"
                     class="sm:col-span-3"/>

          <FormInput name="additional_data.install_video_url_2"
                     label="Install Video 2"
                     v-model="form.additional_data.install_video_url_2"
                     :errors="errors"
                     class="sm:col-span-3"/>

          <FormInput name="additional_data.install_video_url_3"
                     label="Install Video 3"
                     v-model="form.additional_data.install_video_url_3"
                     :errors="errors"
                     class="sm:col-span-3"/>

          <FormInput name="additional_data.install_video_url_4"
                     label="Install Video 4"
                     v-model="form.additional_data.install_video_url_4"
                     :errors="errors"
                     class="sm:col-span-3"/>
        </div>

        <div class="section">
          <div class="sm:col-span-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Options
            </h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">
              Set attributes that will be available when creating a new build.
            </p>
          </div>

          <MultiOptionPicker v-for="(item, index) in options"
                             :key="item.id"
                             class="col-span-6"
                             v-model="form.options[index]"
                             :errors="errors"
                             :option="item" />
        </div>

        <FormActions :cancelUrl="$route('parts.index')"
                     :show-submit="$can('parts.write')"
                     grid />
      </form>
    </FormWrapper>
  </MainLayout>
</template>

<script>
import PageComponent from '@/pages/page-component'
import FormComponent from '@/pages/form-component'
import { Inertia } from '@inertiajs/inertia'
import MultiOptionPicker from '@/components/multi-option-picker.vue'
import UsesPermissions from '@/mixins/uses-permissions'

export default {
  mixins: [
    PageComponent,
    FormComponent,
    UsesPermissions,
  ],

  components: {
    MultiOptionPicker,
  },

  props: {
    model: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    options: Array,
    additionalDataFields: Array,
    errors: Object,
    numberOfImages: Number,
  },

  data() {
    const imageFields = Array(this.numberOfImages)
      .fill(null)
      .flatMap((_, i) => [
          {
            field: `image_filename_${i+1}`,
            label: `Image Filename ${i+1}`,
          },
          {
            field: `image_seo_alt_tag_${i+1}`,
            label: `Image SEO Alt Tag ${i+1}`,
          },
      ]);

    const images = imageFields.reduce(
      (carry, item) => ({
        ...carry,
        [item.field]: this.model?.additional_data?.[item.field],
      }),
      {}
    );

    const additionalData = this.additionalDataFields.reduce(
      (carry, name) => ({
        ...carry,
        [name]: this.model?.additional_data?.[name],
      }),
      {}
    );

    return {
      title: `Parts :: ${this.model?.name}`,
      formSuccess: false,
      form: {
        sku: this.model?.sku,
        handle: this.model?.handle,
        description: this.model?.description,
        retail_price: this.model?.retail_price,
        options: this.options.map(item => item.selected),
        // `images` are new addition
        // we added 3 more image entries
        // and thus we have to make sure the older entries will still work
        additional_data: {
          ...images,
          ...additionalData,
        },
      },
      imageFields,
    }
  },

  methods: {
    submit() {
      Inertia.put(this.$route('parts.update', [this.model.id]), this.getFormData(), {
        preserveState: true,
        onFinish: () => {
          this.$refs.topAnchor.scrollIntoView()
        }
      })
    },

    getFormData() {
      const { options, ...form } = this.form

      form.options = this.form.options.reduce(
        (carry, selected) => {
          return (selected || []).reduce(
            (carry, item) => ({
              ...carry,
              [item.id]: item,
            }),
            carry,
          )
        },
        {}
      );

      if (! Object.keys(form.options).length) {
        form.options = []
      }

      return form
    }
  },
}
</script>
